import { CompanyFilterRepository } from '../repository/CompanyFilterRepository';
import { GzCompanyFilterWebComponent } from '..';
import { Target } from '@nirvana/shared/dtos/types/alerts/common';
import { CompanyFilterEvents } from '../types/types';
import { COMPANIES_LIST_ID, CompanyListEvents } from '../../gzCompanyList/types';
import { DEFAULT_COMPANIES_LIMIT, DEFAULT_COMPANIES_PAGE } from '../../../utils/company.selector.utils';
import { EnumListEvents, EnumListItem, SelectedOption } from '../../gzEnumList/types';
import { CompanySpecialValues } from '@nirvana/shared/dtos/types/shared/filters';
import { GzCheckboxWebComponent } from '../../gzCheckbox';
import { GetAvailableCompaniesParams } from '@nirvana/shared/dtos/types/dashboards/gz-console';

export class CompanyFilterController {
  private dependencies: any;
  private component: GzCompanyFilterWebComponent;
  private dataRepository: CompanyFilterRepository;
  private companySelectorParams: GetAvailableCompaniesParams = {
    includeOwnCompanyIfInTargets: true,
    includeOwnCompanyIfInTargetsOnlyOwnCompany: false,
    excludeCompanion: false,
  };
  private scrollError = new Error('Unable to process scroll');
  private shouldLoadPageOnScroll = true;
  private loadedCompanies = 0;
  private _pageCount = 1;
  private _auxPageCount = 1;

  constructor(dependencies, component) {
    if (this.dependencies) {
      return;
    }

    this.dependencies = dependencies;
    this.buildDataRepository();
    this.component = component;
    this.addEventListeners();
  }

  addEventListeners(): void {
    this.component.addEventListener(CompanyListEvents.SEARCH_COMPANY, this.getCompanies.bind(this));
    this.component.addEventListener(CompanyListEvents.SEARCH_CLEAR, this.onClearSearchBar.bind(this));
    this.component.addEventListener(CompanyListEvents.LOAD_MORE_COMPANIES, this.loadMoreCompanies.bind(this));
    this.component.addEventListener(EnumListEvents.SELECTION_CHANGE, this.handleSelectionChange.bind(this));
    this.component.addEventListener('open', this.handleDropdownOpen.bind(this));
    this.component.addEventListener('close', this.handleDropdownClose.bind(this));
  }

  async getCompanies(event: CustomEvent): Promise<void> {
    this._auxPageCount = 1;
    this.component.handleLoadingState(true);

    try {
      const data = await this.dataRepository.getCompanies({
        companyName: event.detail.searchString,
        params: this.companySelectorParams,
        limit: DEFAULT_COMPANIES_LIMIT,
        page: DEFAULT_COMPANIES_PAGE,
      });
      this.component.handleLoadingState(false);

      if (!data) {
        throw new Error('Unable to query for companies');
      }

      const additionalFilterOptions = this.formatTargetsToFilterOptions(data.targets || [], {});
      this.component.filterOptions = this.component.filterOptions.concat(
        this.removeDuplicateCompanies(additionalFilterOptions)
      );
      this.component.announceCompaniesChanged();
      await this.awaitListRerender();
      this.component.enumList.continueHandleOptionsSearch(event.detail.sourceEvent);
      this.loadedCompanies = this.component.enumList.getNumberOfVisibleCompanies();
    } catch (e) {
      this.component.handleLoadingState(false);
      this.component.isError = true;
      console.error(e);
    }
  }

  async loadMoreCompanies(): Promise<void> {
    const haltLoadMore = !this.shouldLoadPageOnScroll || this.loadedAllItems() || this.component.enumList.isLoading;

    if (haltLoadMore) {
      return;
    }

    if (!this.component.enumList.shadowRoot) {
      throw this.scrollError;
    }

    const companiesList = this.component.enumList.shadowRoot.querySelector(`#${COMPANIES_LIST_ID}`);

    if (!companiesList) {
      throw this.scrollError;
    }

    try {
      if (
        Math.ceil(companiesList.scrollTop + companiesList.clientHeight) === companiesList.scrollHeight &&
        companiesList.scrollTop !== 0
      ) {
        this.pageCount += 1;
        this.component.handleLoadingState(true);

        const data = await this.dataRepository.getCompanies({
          companyName: this.getSearchString(),
          params: this.companySelectorParams,
          limit: DEFAULT_COMPANIES_LIMIT,
          page: this.pageCount,
        });
        this.component.handleLoadingState(false);

        if (!data) {
          throw new Error('Unable to query for companies');
        }

        const additionalFilterOptions = this.formatTargetsToFilterOptions(data.targets || [], {});
        const uniqCompanies = this.removeDuplicateCompanies(additionalFilterOptions);

        if (!uniqCompanies.length) {
          this.loadMoreCompanies();
          return;
        }

        this.component.filterOptions = this.component.filterOptions.concat(uniqCompanies);
        await this.awaitListRerender();
        this.loadedCompanies = this.component.enumList.getNumberOfVisibleCompanies();
        this.component.announceCompaniesChanged();
      }
    } catch (e) {
      this.component.handleLoadingState(false);
      this.pageCount -= 1;
      this.component.isError = true;
      console.error(e);
    }
  }

  async onClearSearchBar(event: CustomEvent): Promise<void> {
    this._auxPageCount = 1;
    await this.awaitListRerender();
    this.component.enumList.continueHandleOptionsSearch(event.detail.sourceEvent);
    this.loadedCompanies = this.component.enumList.getNumberOfVisibleCompanies();
  }

  public async setFilterOptions(selectedValues): Promise<void> {
    try {
      const requiredValuesMissing = this.getMissingRequiredValues(selectedValues);
      if (!this.component.filterOptions.length || requiredValuesMissing.length) {
        await this.awaitListRerender();
        this.component.resetFilterLabel();
        this.component.handleLoadingState(true);
        //Initial load
        const response = await this.dataRepository.getInitialTargets({
          params: {
            ...this.companySelectorParams,
            companyIds: requiredValuesMissing,
          },
        });
        this.component.handleLoadingState(false);

        if (!response) {
          throw new Error('Unable to populate company selector');
        }

        this.component.filterOptions = this.formatTargetsToFilterOptions(response.targets || [], selectedValues);
        this.component.defaultValue = response.defaultTarget;
        this.pageCount = 1;
        this.loadedCompanies = this.component.filterOptions.length;
      }

      // Update selected options
      const filterOptions = this.component.filterOptions.map(filterOption => ({
        ...filterOption,
        selected: !!selectedValues[filterOption.value],
      }));

      const selectedFilterOptions = filterOptions.filter(object => object.selected);
      const unselectedFilterOptions = filterOptions.filter(object => !object.selected);

      this.reorderCompanies(selectedFilterOptions, unselectedFilterOptions);
      this.handleSpecialOptions(selectedValues);
      this.component.announceCompaniesChanged();
    } catch (e) {
      this.component.isError = true;
      console.error(e);
    }
  }

  public removeDuplicateCompanies(companies: EnumListItem[]): EnumListItem[] {
    // We need to filter out new companies that are already in the selector
    return companies.filter(
      company => !this.component.filterOptions.filter(filterOption => filterOption.value === company.value).length
    );
  }

  public getSpecialSelectedOption(specialValue: CompanySpecialValues): SelectedOption[] {
    switch (specialValue) {
      case CompanySpecialValues.ALL:
        return [
          {
            label: this.component.enumList.allItemsLabel,
            value: CompanySpecialValues.ALL,
          },
        ];
      case CompanySpecialValues.ALL_DIRECT_MANAGEMENT:
        return [
          {
            label: this.component.enumList.allDirectManagementLabel,
            value: CompanySpecialValues.ALL_DIRECT_MANAGEMENT,
          },
        ];
      default:
        throw new Error('Value is not special company filter value!');
    }
  }

  private loadedAllItems(): boolean {
    return this.loadedCompanies < this.pageCount * DEFAULT_COMPANIES_LIMIT;
  }

  private buildDataRepository(): void {
    this.dataRepository = new CompanyFilterRepository();
    this.dataRepository.injectDependencies(this.dependencies);
  }

  private formatTargetsToFilterOptions(targets: Array<Target>, selectedValues): EnumListItem[] {
    return targets.map(target => ({
      label: target.companyName,
      value: target.companyId,
      selected: !!selectedValues[target.companyId],
    }));
  }

  private reorderCompanies(selectedFilterOptions, unselectedFilterOptions) {
    unselectedFilterOptions.sort((a, b) => {
      const lowerA = a.label.toLowerCase();
      const lowerB = b.label.toLowerCase();
      if (lowerA === lowerB) return 0;
      return lowerA < lowerB ? -1 : 1;
    });

    this.component.filterOptions = selectedFilterOptions.concat(unselectedFilterOptions);
  }

  private async handleSelectionChange(event: CustomEvent): Promise<void> {
    const selectedValues = event.detail.selectedValues;
    const selectedFilterOptions = this.component.filterOptions.filter(filterOption =>
      selectedValues.includes(filterOption.value)
    );
    const unselectedFilterOptions = this.component.filterOptions.filter(
      filterOption => !selectedValues.includes(filterOption.value)
    );
    //We need to do this in order to solve a race condition
    selectedFilterOptions.forEach(selectedFilterOption => {
      selectedFilterOption.selected = true;
    });
    unselectedFilterOptions.forEach(selectedFilterOption => {
      selectedFilterOption.selected = false;
    });

    this.reorderCompanies(selectedFilterOptions, unselectedFilterOptions);
    this.component.specialValueSelected = selectedValues.find(value =>
      Object.values(CompanySpecialValues).includes(value as CompanySpecialValues)
    );
    this.component.dispatchEvent(
      new CustomEvent(CompanyFilterEvents.COMPANIES_CHANGE, {
        detail: {
          selectedFilterOptions,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  //We are scrolling the list to the top, so that the user is not left at the bottom of the filter
  private async handleDropdownOpen(): Promise<void> {
    if (!this.component.enumList.shadowRoot) {
      return;
    }

    const companiesList = this.component.enumList.shadowRoot.querySelector(`#${COMPANIES_LIST_ID}`);

    if (!companiesList) {
      return;
    }

    await this.awaitListRerender();
    companiesList.scrollTop = 0;
  }

  private async handleDropdownClose(): Promise<void> {
    if (this.component.isError && this.loadedCompanies !== 0) {
      this.component.isError = false;
    }
  }

  public handleSpecialOptions(selectedValues): void {
    // The selection needs to be exclusive
    this.component.enumList.unselectSpecialOptions();

    const specialOption = Object.keys(selectedValues).find(value =>
      Object.values(CompanySpecialValues).includes(value as CompanySpecialValues)
    );
    this.component.specialValueSelected = specialOption;
    if (!specialOption) {
      return;
    }

    const checkbox = this.component.enumList.checkboxesList.find(
      (cbox: GzCheckboxWebComponent) => cbox.value === specialOption
    ) as GzCheckboxWebComponent;

    if (!checkbox) {
      throw Error('Unable to select special company filter!');
    }

    checkbox.checked = true;
  }

  private getMissingRequiredValues(selectedValues): Array<string> {
    const filterValues = this.component.filterOptions.map(option => option.value);
    const requiredValues = Object.keys(selectedValues).filter(
      value => !Object.values(CompanySpecialValues).includes(value as CompanySpecialValues)
    );
    return requiredValues.filter(value => !filterValues.includes(value));
  }

  private async awaitListRerender(): Promise<void> {
    await this.component.updateComplete;
    await this.component.enumList.updateComplete;
  }

  private getSearchString(): string {
    return this.component.enumList.searchInput.value;
  }

  private hasSearchString(): boolean {
    return this.getSearchString() !== '';
  }

  get pageCount(): number {
    if (this.hasSearchString()) {
      return this._auxPageCount;
    }
    return this._pageCount;
  }

  set pageCount(value: number) {
    if (this.hasSearchString()) {
      this._auxPageCount = value;
      return;
    }
    this._pageCount = value;
  }
}
